<template>
    <Modal size="md" @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">
                <span v-if="tag.item"> Update </span>
                <span v-else> Create </span>
                merge tag
            </div>
        </template>

        <template #body>
            <div class="p-6">
                <SelectInput
                    v-if="!tag.item"
                    v-model="type"
                    class="mb-6"
                    placeholder="Select type"
                    label="Type"
                    :data="types"
                />

                <div v-if="tag.hasItems || type === 'nested'" class="mb-6">
                    <TextInput
                        v-model="v$.data.name.$model"
                        label="Name"
                        placeholder="Name"
                        required
                        :error="getError('name')"
                    />
                </div>
                <div v-else class="flex flex-col gap-7">
                    <TextInput
                        v-model="v$.data.text.$model"
                        label="Text"
                        placeholder="Text"
                        required
                        :error="getError('text')"
                    >
                        <template #help>
                            Shown text in the editor menu
                        </template>
                    </TextInput>
                    <TextInput
                        v-model="v$.data.label.$model"
                        label="Label"
                        placeholder="Label"
                        required
                        :error="getError('label')"
                    >
                        <template #help>
                            Shown description title in the editor menu
                        </template>
                    </TextInput>

                    <TextInput
                        v-model="v$.data.value.$model"
                        label="Value"
                        placeholder="Value"
                        required
                        :error="getError('value')"
                    >
                        <template #help>
                            Value of the merge tag without syntax
                        </template>
                    </TextInput>

                    <div class="mt-1 text-gray-600">
                        Merge tag preview:
                        <div
                            v-if="state.data.value"
                            class="inline-flex px-2 text-lg text-gray-800"
                            :class="{
                                'border-2 border-dashed border-indigo-600':
                                    form?.smart_merge_tags,
                            }"
                        >
                            <div>{{ formattedValue }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton color="white" size="md" @click="$emit('close')">
                    Cancel
                </AtomicButton>

                <AtomicButton
                    v-if="tag.item"
                    size="md"
                    color="yellow"
                    @click="updateTag"
                >
                    Update
                </AtomicButton>

                <AtomicButton
                    v-else
                    size="md"
                    color="yellow"
                    @click="createTag"
                >
                    Create
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts" setup>
import IInputErrors from '@/types/Atomic/IInputErrors';
import IEditorSettings from '@/types/Settings/IEditorSettings';
import useVuelidate from '@vuelidate/core';
import { maxLength } from '@vuelidate/validators';
import { requiredIf } from '@vuelidate/validators';
import { PropType } from 'vue';

const props = defineProps({
    tag: {
        type: Object,
        required: true,
    },
    form: {
        type: Object as PropType<IEditorSettings>,

        default: () => {},
    },
});

const emit = defineEmits(['close', 'update', 'store']);

const formattedValue = computed(() => {
    let value = state?.data?.value || '';

    const specialCharRegex = /^[*|[\]\-{}<>/]+|[*|[\]\-{}<>/]+$/g;

    if (props.form?.smart_merge_tags) {
        // Remove special chars only at the start and end
        value = value.replace(specialCharRegex, '');

        // Use custom syntax
        return `${props.form.smart_merge_tags_syntax_start}${value}${props.form.smart_merge_tags_syntax_end}`;
    } else {
        // Check if value already has special chars at the start and end
        if (!specialCharRegex.test(value)) {
            return `*|${value}|*`;
        }
        return value; // Do nothing if special chars are already present at the start and end
    }
});

onMounted(() => {
    if (props.tag.itemIndex) {
        types.value.splice(1, 1);
    }
});

const errors = ref<IInputErrors>({});
const state = reactive({
    data: {
        name: props.tag.item?.name ?? '',
        value: props.tag.item?.value ?? '',
        text: props.tag.item?.text ?? '',
        label: props.tag.item?.label ?? '',
    },
});
const rules = {
    data: {
        name: {
            required: requiredIf(
                () => props.tag.hasItems || type.value === 'nested'
            ),
            maxLength: maxLength(255),
        },
        value: {
            required: requiredIf(
                () => !props.tag.hasItems && type.value === 'item'
            ),
            maxLength: maxLength(255),
        },
        text: {
            required: requiredIf(
                () => !props.tag.hasItems && type.value === 'item'
            ),
            maxLength: maxLength(255),
        },
        label: {
            required: requiredIf(
                () => !props.tag.hasItems && type.value === 'item'
            ),
            maxLength: maxLength(255),
        },
    },
};
const v$ = useVuelidate(rules, state);

const type = ref('item');

const types = ref([
    { value: 'item', title: 'Merge tag item' },
    { value: 'nested', title: 'Merge tags nested' },
]);
const createTag = () => {
    v$.value.data.$touch();
    if (v$.value.data.$error) {
        return;
    } else {
        if (type.value === 'nested') {
            state.data.text = '';
            state.data.label = '';
            state.data.value = '';
        } else {
            state.data.name = '';
        }
        emit('store', state.data);
    }
};

const updateTag = () => {
    v$.value.data.$touch();
    if (v$.value.data.$error) {
        return;
    } else {
        emit('update', state.data);
    }
};

const getError = (field: string) => {
    return (
        v$.value.data[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ''
    );
};
</script>
